/**
 * ONE gate for whether this session's activity counts as real product usage.
 *
 * Why this exists (investigated 2026-08-22): we were recording synthetic traffic as real.
 *  - `scripts/prerender.js` runs headless Chromium against the BUILT app on every deploy, and that build has
 *    `REACT_APP_ENABLE_ANALYTICS=true`. Booting the SPA initialises Firebase Analytics, which auto-collects
 *    `page_view` / `session_start` / `first_visit` for every prerendered route — so each deploy manufactured a
 *    burst of "visitors" indistinguishable from real ones.
 *  - `telemetryService.track()` had NO environment gate at all and fired BEFORE the GA4 guard, so the same
 *    prerender pass also wrote into the real `telemetry_events` spine, and any local `npm start` without the
 *    emulators wrote straight into a deployed backend.
 *
 * The signal is AUTOMATION, not user-agent strings: every browser driven by WebDriver/CDP (Playwright,
 * Puppeteer, Selenium — so our prerender, our E2E and any future UI verification run) sets
 * `navigator.webdriver = true`. That is a spec'd property, not a heuristic, and it costs nothing to check.
 *
 * Kept deliberately separate from "is GA4 configured" (`REACT_APP_ENABLE_ANALYTICS`): that answers CAN we
 * send, this answers SHOULD we. Both must be true to record a real event.
 */
export function isAutomatedSession(): boolean {
  if (typeof navigator === 'undefined') return true;         // no browser at all (SSR/jest) — never real usage
  return navigator.webdriver === true;
}

/** True when this session's events represent a REAL person and should be recorded. */
export function countsAsRealUsage(): boolean {
  return !isAutomatedSession();
}
